/***************************************************************************************
 * FileName      : quad-image.ts
 * Description   : 4개 꼭짓점 좌표 기반 이미지 배치 타입 정의
 *                 - 사각형(Quad) 꼭짓점 좌표, 배치 설정, 노드 관리
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.02.11
 * Modified Date :
 * Reference     : src/07-image-quad-placement/src/types/quad-image.ts
 ***************************************************************************************/

// ============================================================================
// 3D 좌표 타입
// ============================================================================

/** 3D 위치 벡터 */
export interface Vector3 {
  x: number;
  y: number;
  z: number;
}

// ============================================================================
// 사각형 꼭짓점 좌표
// ============================================================================

/**
 * 2026.02.11 : 박은정 : 4개 꼭짓점으로 정의되는 사각형(Quad) 좌표
 * 각 꼭짓점은 Matterport 3D 공간의 절대 좌표(Anchor Position)
 */
export interface QuadCorners {
  /** 좌측 상단 좌표 */
  topLeft: Vector3;
  /** 우측 상단 좌표 */
  topRight: Vector3;
  /** 좌측 하단 좌표 */
  bottomLeft: Vector3;
  /** 우측 하단 좌표 */
  bottomRight: Vector3;
}

// ============================================================================
// 이미지 배치 설정
// ============================================================================

/**
 * 2026.02.11 : 박은정 : 4개 꼭짓점 기반 이미지 배치 설정
 */
export interface QuadImagePlacement {
  /** 고유 ID */
  id: string;
  /** 이미지 표시명 */
  name: string;
  /** 이미지 URL (data URI 또는 HTTP) */
  imageUrl: string;
  /** 4개 꼭짓점 좌표 */
  corners: QuadCorners;
  /** 투명도 (0.0 ~ 1.0) */
  opacity: number;
  /** 표시 여부 */
  visible: boolean;
  /** 텍스처 해상도 (픽셀) */
  textureSize?: { w: number; h: number };
}

// ============================================================================
// 이미지 노드 관리
// ============================================================================

/** 이미지 노드 관리 정보 */
export interface QuadImageNodeInfo {
  /** 이미지 ID */
  id: string;
  /** 배치 설정 */
  placement: QuadImagePlacement;
  /** Matterport Scene Node */
  node: any;
  /** mp.quadImagePlane 컴포넌트 참조 */
  component: any;
}

// ============================================================================
// 계산 유틸리티
// ============================================================================

/**
 * 2026.02.11 : 박은정 : 4개 꼭짓점의 중심점 계산
 */
export function computeQuadCenter(corners: QuadCorners): Vector3 {
  return {
    x: (corners.topLeft.x + corners.topRight.x + corners.bottomLeft.x + corners.bottomRight.x) / 4,
    y: (corners.topLeft.y + corners.topRight.y + corners.bottomLeft.y + corners.bottomRight.y) / 4,
    z: (corners.topLeft.z + corners.topRight.z + corners.bottomLeft.z + corners.bottomRight.z) / 4,
  };
}

/**
 * 2026.02.11 : 박은정 : 꼭짓점을 중심점 기준 로컬 좌표로 변환
 */
export function cornersToLocal(corners: QuadCorners, center: Vector3): QuadCorners {
  return {
    topLeft: { x: corners.topLeft.x - center.x, y: corners.topLeft.y - center.y, z: corners.topLeft.z - center.z },
    topRight: { x: corners.topRight.x - center.x, y: corners.topRight.y - center.y, z: corners.topRight.z - center.z },
    bottomLeft: { x: corners.bottomLeft.x - center.x, y: corners.bottomLeft.y - center.y, z: corners.bottomLeft.z - center.z },
    bottomRight: { x: corners.bottomRight.x - center.x, y: corners.bottomRight.y - center.y, z: corners.bottomRight.z - center.z },
  };
}

/**
 * 2026.02.11 : 박은정 : 두 점 사이의 거리 계산
 */
export function distance3D(a: Vector3, b: Vector3): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dz = b.z - a.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

// ============================================================================
// 상수 정의
// ============================================================================

/** 기본 텍스처 해상도 */
export const DEFAULT_TEXTURE_SIZE = { w: 1024, h: 1024 };

/** 기본 투명도 */
export const DEFAULT_OPACITY = 1.0;

